import { useEffect, useState } from "react";
import { api, apiErrorMessage } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Copy, KeyRound, Webhook, BookOpen, ShieldOff, Loader2, FileText, Download, ExternalLink } from "lucide-react";

const ENDPOINT_DOCS = [
  {
    method: "GET",
    path: "/api/report/clicks",
    desc: "Raw click events (one row per outbound click).",
    params: ["from", "to", "product_id", "sharer_id", "user_id", "cursor", "limit"],
  },
  {
    method: "GET",
    path: "/api/report/conversions",
    desc: "Partner-confirmed conversions (postbacks).",
    params: ["from", "to", "product_id", "partner_id", "status", "cursor", "limit"],
  },
  {
    method: "GET",
    path: "/api/report/customers",
    desc: "Customers captured via mobile-capture flow.",
    params: ["from", "to", "cursor", "limit"],
  },
  {
    method: "GET",
    path: "/api/report/products",
    desc: "Product & partner catalog.",
    params: ["type", "partner_id", "active"],
  },
  {
    method: "GET",
    path: "/api/report/referrals",
    desc: "Referral tree — clicks & unique recipients grouped by sharer_id.",
    params: ["sharer_id", "from", "to"],
  },
  {
    method: "GET",
    path: "/api/report/stats/daily",
    desc: "Daily rollup: clicks, unique users, conversions, conversion rate.",
    params: ["from", "to", "product_id"],
  },
];

function copyText(text, label = "Copied") {
  if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(text).then(() => toast.success(label));
  else window.prompt("Copy:", text);
}

function ApiKeysCard() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [freshKey, setFreshKey] = useState(null);

  const load = () => api.get("/admin/api-keys").then((r) => setRows(r.data?.results || []));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Enter a key name");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/api-keys", { name });
      setFreshKey(data);
      setName("");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? BI tools using it will lose access immediately.")) return;
    try { await api.post(`/admin/api-keys/${id}/revoke`); toast.success("Key revoked"); await load(); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  };

  return (
    <Card data-testid="dev-api-keys-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4" /> API keys</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            data-testid="dev-key-name"
            placeholder="Key name (e.g. Metabase-prod)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button data-testid="dev-key-create" onClick={create} disabled={busy}>
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Mint key
          </Button>
        </div>
        {freshKey && (
          <div className="rounded-lg border border-[#0055FF]/30 bg-[#0055FF]/5 p-4 space-y-2" data-testid="dev-fresh-key">
            <div className="text-xs font-semibold text-[#0055FF]">Copy now — this key will never be shown again.</div>
            <div className="flex items-center gap-2 font-mono text-sm">
              <code className="flex-1 break-all">{freshKey.key}</code>
              <Button size="sm" variant="outline" onClick={() => copyText(freshKey.key, "Key copied")}>
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
        <div className="border rounded-lg divide-y">
          {rows.length === 0 && <div className="p-4 text-sm text-neutral-500">No keys yet.</div>}
          {rows.map((k) => (
            <div key={k.id} className="p-3 flex items-center gap-3 text-sm">
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{k.name}</div>
                <div className="text-xs text-neutral-500 font-mono">
                  {k.prefix}… · created {new Date(k.created_at).toLocaleDateString()}
                  {k.last_used_at && ` · last used ${new Date(k.last_used_at).toLocaleString()}`}
                </div>
              </div>
              {k.revoked ? (
                <Badge variant="secondary">Revoked</Badge>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => revoke(k.id)}>
                  <ShieldOff className="w-3.5 h-3.5 mr-1" /> Revoke
                </Button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function WebhooksCard() {
  const [rows, setRows] = useState([]);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [freshHook, setFreshHook] = useState(null);

  const load = () => api.get("/admin/webhooks").then((r) => setRows(r.data?.results || []));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!url.startsWith("http")) return toast.error("Enter a full https URL");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/webhooks", { url });
      setFreshHook(data);
      setUrl("");
      await load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this webhook? Events will stop firing to it.")) return;
    try { await api.post(`/admin/webhooks/${id}/revoke`); toast.success("Webhook revoked"); await load(); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  };

  return (
    <Card data-testid="dev-webhooks-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Webhook className="w-4 h-4" /> Webhooks</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            data-testid="dev-webhook-url"
            placeholder="https://your-tool.com/hook"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Button data-testid="dev-webhook-create" onClick={create} disabled={busy}>
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Register
          </Button>
        </div>
        {freshHook && (
          <div className="rounded-lg border border-[#0055FF]/30 bg-[#0055FF]/5 p-4 space-y-2" data-testid="dev-fresh-hook">
            <div className="text-xs font-semibold text-[#0055FF]">Signing secret — copy now, we do not show it again.</div>
            <div className="flex items-center gap-2 font-mono text-sm">
              <code className="flex-1 break-all">{freshHook.secret}</code>
              <Button size="sm" variant="outline" onClick={() => copyText(freshHook.secret, "Secret copied")}>
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="text-[11px] text-neutral-600">
              We POST JSON events to your URL with header <code>X-Offer-Signature</code> = HMAC-SHA256(secret, body).
            </div>
          </div>
        )}
        <div className="border rounded-lg divide-y">
          {rows.length === 0 && <div className="p-4 text-sm text-neutral-500">No webhooks yet.</div>}
          {rows.map((h) => (
            <div key={h.id} className="p-3 flex items-center gap-3 text-sm">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-xs truncate">{h.url}</div>
                <div className="text-xs text-neutral-500">
                  events: {(h.events || []).join(", ")} · last status: {h.last_status ?? "—"}
                </div>
              </div>
              {h.revoked ? <Badge variant="secondary">Revoked</Badge> : (
                <Button size="sm" variant="ghost" onClick={() => revoke(h.id)}>
                  <ShieldOff className="w-3.5 h-3.5 mr-1" /> Revoke
                </Button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DocsCard() {
  const base = typeof window !== "undefined" ? `${window.location.protocol}//${window.location.host}` : "";
  return (
    <Card data-testid="dev-docs-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><BookOpen className="w-4 h-4" /> Reporting API — endpoints</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-neutral-600">
          Every endpoint requires <code>X-API-Key</code> header. Cursor pagination — use the <code>next_cursor</code>
          value from a response as <code>cursor=…</code> in the next call.
        </div>
        <div className="space-y-3">
          {ENDPOINT_DOCS.map((d) => {
            const curl = `curl "${base}${d.path}?limit=50" \\\n  -H "X-API-Key: <your_key>"`;
            return (
              <div key={d.path} className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="font-mono text-[10px]">{d.method}</Badge>
                  <code className="text-sm font-mono">{d.path}</code>
                </div>
                <div className="text-xs text-neutral-600 mb-2">{d.desc}</div>
                <div className="text-[11px] text-neutral-500 mb-2">
                  Query params: <span className="font-mono">{d.params.join(", ")}</span>
                </div>
                <div className="flex items-start gap-2">
                  <pre className="flex-1 text-[11px] bg-neutral-50 rounded p-2 overflow-x-auto whitespace-pre">{curl}</pre>
                  <Button size="sm" variant="outline" onClick={() => copyText(curl, "cURL copied")}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function DocumentsCard() {
  const base = typeof window !== "undefined" ? `${window.location.protocol}//${window.location.host}` : "";
  const openapiUrl = `${base}/api/openapi.json`;
  const swaggerUrl = `${base}/docs`;
  const quickstart = `# 1. Mint an API key (Developer → API keys → Mint)
export OE_KEY="oe_xxxxxxxxxxxxxxxxxxx"

# 2. Call any /api/report/* endpoint with X-API-Key
curl "${base}/api/report/clicks?limit=50" -H "X-API-Key: $OE_KEY"

# 3. Paginate via next_cursor from the response body
curl "${base}/api/report/clicks?limit=50&cursor=<next_cursor>" \\
  -H "X-API-Key: $OE_KEY"

# 4. Register a webhook (Developer → Webhooks → Register)
#    You will receive POSTs signed with HMAC-SHA256 in X-Offer-Signature.
#    Verify: hmac(secret, raw_body).hexdigest() === request.headers['x-offer-signature']`;

  return (
    <Card data-testid="dev-documents-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FileText className="w-4 h-4" /> Documents</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-neutral-600">
          Full API specification + auto-generated interactive docs. Use these to onboard a partner or wire up a BI tool.
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <a
            href={openapiUrl}
            download="offer-express-openapi.json"
            data-testid="dev-doc-openapi"
            className="flex items-start gap-3 p-3 rounded-lg border hover:border-black transition-colors"
          >
            <Download className="w-4 h-4 mt-0.5 text-[#0055FF]" />
            <div className="min-w-0">
              <div className="text-sm font-medium">OpenAPI 3.1 spec</div>
              <div className="text-xs text-neutral-500 truncate">Machine-readable JSON — import into Postman, Insomnia, Stoplight, or codegen SDKs.</div>
            </div>
          </a>

          <a
            href={swaggerUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="dev-doc-swagger"
            className="flex items-start gap-3 p-3 rounded-lg border hover:border-black transition-colors"
          >
            <ExternalLink className="w-4 h-4 mt-0.5 text-[#0055FF]" />
            <div className="min-w-0">
              <div className="text-sm font-medium">Swagger UI (Try it live)</div>
              <div className="text-xs text-neutral-500 truncate">Interactive browser — hit endpoints without leaving the panel.</div>
            </div>
          </a>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Quick start</div>
            <Button size="sm" variant="outline" onClick={() => copyText(quickstart, "Snippet copied")}>
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy
            </Button>
          </div>
          <pre className="text-[11px] bg-neutral-50 rounded p-3 overflow-x-auto whitespace-pre leading-relaxed">{quickstart}</pre>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          <div className="border rounded-lg p-3">
            <div className="font-semibold mb-1">Authentication</div>
            <div className="text-neutral-600">Every request needs an <code className="bg-neutral-100 px-1 rounded">X-API-Key</code> header. Keys are scoped and revocable from this panel.</div>
          </div>
          <div className="border rounded-lg p-3">
            <div className="font-semibold mb-1">Pagination</div>
            <div className="text-neutral-600">Use <code className="bg-neutral-100 px-1 rounded">cursor</code> + <code className="bg-neutral-100 px-1 rounded">limit</code>. Follow <code className="bg-neutral-100 px-1 rounded">next_cursor</code> from each response until null.</div>
          </div>
          <div className="border rounded-lg p-3">
            <div className="font-semibold mb-1">Webhooks</div>
            <div className="text-neutral-600">POST JSON body <code className="bg-neutral-100 px-1 rounded">{"{event, sent_at, data}"}</code>. Verify <code className="bg-neutral-100 px-1 rounded">X-Offer-Signature</code> HMAC-SHA256.</div>
          </div>
          <div className="border rounded-lg p-3">
            <div className="font-semibold mb-1">Rate limits</div>
            <div className="text-neutral-600">Fair-use limit is 500 req/min per key. Contact us if you need more.</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DeveloperSection() {
  return (
    <div className="grid gap-4" data-testid="developer-section">
      <ApiKeysCard />
      <WebhooksCard />
      <DocsCard />
      <DocumentsCard />
    </div>
  );
}
